import { createRouter, createWebHashHistory, isNavigationFailure } from 'vue-router'
import Login from '@/views/Login.vue'
import MainLayout from '@/layout/MainLayout.vue'
import Dashboard from '@/views/Dashboard.vue'
import { syncDocumentTitle } from '@/config/documentTitle'
import { canAccessRoute } from '@/utils/permissionMap'
import { ElMessage } from 'element-plus'

/** 将「被新导航取消」「重复导航」等转为已处理，避免控制台/监控报未捕获的 Promise */
function patchRouterPromiseMethod(router, name) {
  const original = router[name].bind(router)
  router[name] = function (location) {
    return original(location).catch((err) => {
      if (isNavigationFailure(err)) return err
      throw err
    })
  }
}

const ProductCategory = () => import('@/views/product/ProductCategory.vue')
const ProductSeries = () => import('@/views/product/ProductSeries.vue')
const ProductUnit = () => import('@/views/product/ProductUnit.vue')
const ProductTag = () => import('@/views/product/ProductTag.vue')
const ProductBrand = () => import('@/views/product/ProductBrand.vue')
const ProductSpec = () => import('@/views/product/ProductSpec.vue')
const ProductDeviceType = () => import('@/views/product/ProductDeviceType.vue')
const ProductDeviceFeatureDict = () => import('@/views/product/ProductDeviceFeatureDict.vue')
const ProductList = () => import('@/views/product/ProductList.vue')
const ProductForm = () => import('@/views/product/ProductForm.vue')
const WorkorderPrice = () => import('@/views/product/WorkorderPrice.vue')
const ProductLinkDevice = () => import('@/views/product/ProductLinkDevice.vue')
const WaterPackageList = () => import('@/views/product/WaterPackageList.vue')
const WaterPackageForm = () => import('@/views/product/WaterPackageForm.vue')

const ServicePackageList = () => import('@/views/product/ServicePackageList.vue')
const ServicePackageForm = () => import('@/views/product/ServicePackageForm.vue')
const BundleProductList = () => import('@/views/product/BundleProductList.vue')
const BundleProductForm = () => import('@/views/product/BundleProductForm.vue')

const ReportPlaceholder = () => import('@/views/report/ReportPlaceholder.vue')
const PaymentCollectionStatistics = () => import('@/views/report/PaymentCollectionStatistics.vue')
const InstallationStatistics = () => import('@/views/report/InstallationStatistics.vue')
const SalesStatistics = () => import('@/views/report/SalesStatistics.vue')
const EmployeeStatistics = () => import('@/views/report/EmployeeStatistics.vue')
const DealerAnalysisReport = () => import('@/views/report/DealerAnalysisReport.vue')
const DeviceSalesStatistics = () => import('@/views/report/DeviceSalesStatistics.vue')
const OperationsStatisticsReport = () => import('@/views/report/OperationsStatisticsReport.vue')
const ServicePackStockAnalysis = () => import('@/views/report/ServicePackStockAnalysis.vue')
const LeaseDeviceStockAnalysis = () => import('@/views/report/LeaseDeviceStockAnalysis.vue')

const REPORT_V2_COMPONENT_BY_SUFFIX = {
  'payment-collection': PaymentCollectionStatistics,
  'installation-stats': InstallationStatistics,
  'sales-stats': SalesStatistics,
  'employee-stats': EmployeeStatistics,
  'dealer-analysis': DealerAnalysisReport,
  'device-sales-stats': DeviceSalesStatistics,
  'ops-statistics': OperationsStatisticsReport,
  'service-pack-stock': ServicePackStockAnalysis,
  'lease-device-stock': LeaseDeviceStockAnalysis,
}

/** 经营报表：占位路由 path 后缀、路由 name、菜单/页签标题（与 mega 菜单文案一致） */
const REPORT_BIZ_V2_ENTRIES = [
  ['payment-collection', 'ReportV2PaymentCollection', '回款统计表'],
  ['installation-stats', 'ReportV2InstallationStats', '装机统计表'],
  ['sales-stats', 'ReportV2SalesStats', '销售统计表'],
  ['employee-stats', 'ReportV2EmployeeStats', '员工统计表'],
  ['dealer-analysis', 'ReportV2DealerAnalysis', '经销商分析表'],
  ['device-sales-stats', 'ReportV2DeviceSalesStats', '设备销售统计表'],
  ['ops-statistics', 'ReportV2OpsStatistics', '运维统计表'],
  ['lease-device-stock', 'ReportV2LeaseDeviceStock', '租赁设备保有量分析'],
  ['service-pack-stock', 'ReportV2ServicePackStock', '服务包保有量分析'],
]

const reportBizV2Routes = REPORT_BIZ_V2_ENTRIES.map(([pathSuffix, name, title]) => ({
  path: `report/v2/${pathSuffix}`,
  name,
  component: REPORT_V2_COMPONENT_BY_SUFFIX[pathSuffix] || ReportPlaceholder,
  meta: { title },
}))

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      redirect: '/login',
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
    },
    {
      path: '/main',
      component: MainLayout,
      children: [
        {
          path: '',
          redirect: 'dashboard',
        },
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard,
        },
        {
          path: 'product/category',
          name: 'ProductCategory',
          component: ProductCategory,
        },
        {
          path: 'product/series',
          name: 'ProductSeries',
          component: ProductSeries,
        },
        {
          path: 'product/unit',
          name: 'ProductUnit',
          component: ProductUnit,
        },
        {
          path: 'product/tag',
          name: 'ProductTag',
          component: ProductTag,
        },
        {
          path: 'product/brand',
          name: 'ProductBrand',
          component: ProductBrand,
        },
        {
          path: 'product/service-package-benefit',
          name: 'ServicePackageBenefit',
          component: () => import('@/views/product/ServicePackageBenefit.vue'),
        },
        {
          path: 'product/filter-type',
          name: 'FilterType',
          component: () => import('@/views/product/FilterType.vue'),
        },
        {
          path: 'product/spec',
          name: 'ProductSpec',
          component: ProductSpec,
        },
        {
          path: 'product/device-type',
          name: 'ProductDeviceType',
          component: ProductDeviceType,
        },
        {
          path: 'product/device-power-supply',
          name: 'ProductDevicePowerSupply',
          component: ProductDeviceFeatureDict,
          meta: { dictKey: 'powerSupply', title: '设备电源功率管理' },
        },
        {
          path: 'product/device-tank-capacity',
          name: 'ProductDeviceTankCapacity',
          component: ProductDeviceFeatureDict,
          meta: { dictKey: 'tankCapacity', title: '设备水箱容量管理' },
        },
        {
          path: 'product/device-faucet-combo',
          name: 'ProductDeviceFaucetCombo',
          component: ProductDeviceFeatureDict,
          meta: { dictKey: 'faucetCount', title: '设备龙头组合管理' },
        },
        {
          path: 'product/list',
          name: 'ProductList',
          component: ProductList,
        },
        {
          path: 'product/add',
          name: 'ProductAdd',
          component: ProductForm,
        },
        {
          path: 'product/edit/:id',
          name: 'ProductEdit',
          component: ProductForm,
        },
        {
          path: 'product/detail/:id',
          name: 'ProductDetail',
          component: ProductForm,
        },
        {
          path: 'product/workorder-price/:id',
          name: 'WorkorderPrice',
          component: WorkorderPrice,
        },
        {
          path: 'product/link-device/:productId',
          name: 'ProductLinkDevice',
          component: ProductLinkDevice,
        },
        {
          path: 'product/water-package/list',
          name: 'WaterPackageList',
          component: WaterPackageList,
        },
        {
          path: 'product/water-package/add',
          name: 'WaterPackageAdd',
          component: WaterPackageForm,
        },
        {
          path: 'product/water-package/edit/:id',
          name: 'WaterPackageEdit',
          component: WaterPackageForm,
        },
        {
          path: 'product/water-package/detail/:id',
          name: 'WaterPackageDetail',
          component: WaterPackageForm,
        },
        {
          path: 'product/service-package/list',
          name: 'ServicePackageList',
          component: ServicePackageList,
        },
        {
          path: 'product/service-package/add',
          name: 'ServicePackageAdd',
          component: ServicePackageForm,
        },
        {
          path: 'product/service-package/edit/:id',
          name: 'ServicePackageEdit',
          component: ServicePackageForm,
        },
        {
          path: 'product/service-package/detail/:id',
          name: 'ServicePackageDetail',
          component: ServicePackageForm,
        },
        {
          path: 'product/bundle/list',
          name: 'BundleProductList',
          component: BundleProductList,
        },
        {
          path: 'product/bundle/add',
          name: 'BundleProductAdd',
          component: BundleProductForm,
        },
        {
          path: 'product/bundle/edit/:id',
          name: 'BundleProductEdit',
          component: BundleProductForm,
        },

        {
          path: 'product/water-package-purchase',
          redirect: '/main/product/water-package/list',
        },
        {
          path: 'user/list',
          name: 'UserList',
          component: () => import('@/views/user/UserList.vue'),
        },
        {
          path: 'user/create',
          name: 'UserCreate',
          component: () => import('@/views/user/UserForm.vue'),
        },
        {
          path: 'user/edit/:id',
          name: 'UserEdit',
          component: () => import('@/views/user/UserForm.vue'),
        },
        {
          path: 'user/detail/:id',
          name: 'UserDetail',
          component: () => import('@/views/user/UserDetail.vue'),
        },
        {
          path: 'user/:userId/contract/:contractId',
          name: 'ContractDetail',
          component: () => import('@/views/contract/ContractDetail.vue'),
        },
        {
          path: 'user/:userId/contract-traffic/:contractId',
          name: 'ContractTrafficDetail',
          component: () => import('@/views/contract/ContractTrafficDetail.vue'),
        },
        {
          path: 'user/:userId/contract-service/:contractId',
          name: 'ContractServiceDetail',
          component: () => import('@/views/contract/ContractServiceDetail.vue'),
        },
        {
          path: 'order/detail/:orderId',
          name: 'OrderDetail',
          component: () => import('@/views/order/OrderDetail.vue'),
        },
        {
          path: 'order/approval-flow',
          name: 'OrderApprovalFlowList',
          component: () => import('@/views/order/ApprovalFlowList.vue'),
        },
        {
          path: 'order/approval-flow/add',
          name: 'OrderApprovalFlowAdd',
          component: () => import('@/views/order/ApprovalFlowEdit.vue'),
        },
        {
          path: 'order/approval-flow/edit/:id',
          name: 'OrderApprovalFlowEdit',
          component: () => import('@/views/order/ApprovalFlowEdit.vue'),
        },
        {
          path: 'order/settings',
          name: 'OrderSettings',
          component: () => import('@/views/order/OrderSettings.vue'),
        },
        {
          path: 'order/recharge/list',
          name: 'RechargeOrderList',
          component: () => import('@/views/order/RechargeOrderList.vue'),
        },
        {
          path: 'order/recharge/detail/:id',
          name: 'RechargeOrderDetail',
          component: () => import('@/views/order/RechargeOrderDetail.vue'),
        },
        {
          path: 'order/lease-renewal/list',
          name: 'LeaseRenewalOrderList',
          component: () => import('@/views/order/LeaseRenewalOrderList.vue'),
        },
        {
          path: 'order/lease-renewal/detail/:id',
          name: 'LeaseRenewalOrderDetail',
          component: () => import('@/views/order/LeaseRenewalOrderDetail.vue'),
        },
        {
          path: 'order/cancel/list',
          name: 'CancelOrderList',
          component: () => import('@/views/order/CancelOrderList.vue'),
        },
        {
          path: 'order/cancel/detail/:id',
          name: 'CancelOrderDetail',
          component: () => import('@/views/order/CancelOrderDetail.vue'),
        },
        {
          path: 'order/after-sales-return/list',
          name: 'AfterSalesReturnOrderList',
          component: () => import('@/views/order/AfterSalesReturnOrderList.vue'),
        },
        {
          path: 'order/after-sales-return/add',
          name: 'AfterSalesReturnOrderForm',
          component: () => import('@/views/order/AfterSalesReturnOrderForm.vue'),
        },
        {
          path: 'order/after-sales-return/detail/:id',
          name: 'AfterSalesReturnOrderDetail',
          component: () => import('@/views/order/AfterSalesReturnOrderDetail.vue'),
        },
        {
          path: 'order/bundle-product/list',
          name: 'BundleProductOrderList',
          component: () => import('@/views/order/BundleProductOrderList.vue'),
        },
        {
          path: 'order/bundle-product/add',
          name: 'BundleProductOrderAdd',
          component: () => import('@/views/order/BundleProductOrderForm.vue'),
        },
        {
          path: 'order/bundle-product/detail/:id',
          name: 'BundleProductOrderDetail',
          component: () => import('@/views/order/BundleProductOrderDetail.vue'),
        },
        {
          path: 'order/bundle-product/edit/:id',
          name: 'BundleProductOrderEdit',
          component: () => import('@/views/order/BundleProductOrderForm.vue'),
        },
        {
          path: 'order/service-package/list',
          name: 'ServicePackageOrderList',
          component: () => import('@/views/order/ServicePackageOrderList.vue'),
        },
        {
          path: 'order/service-package/detail/:id',
          name: 'ServicePackageOrderDetail',
          component: () => import('@/views/order/ServicePackageOrderDetail.vue'),
        },
        {
          path: 'order/sp-shipment-application/list',
          name: 'SpShipmentApplicationList',
          component: () => import('@/views/order/SpShipmentApplicationList.vue'),
        },
        {
          path: 'order/sp-shipment-application/detail/:id',
          name: 'SpShipmentApplicationDetail',
          component: () => import('@/views/order/SpShipmentApplicationDetail.vue'),
        },
        {
          path: 'order/sales-outbound/list',
          name: 'SalesOutboundList',
          component: () => import('@/views/order/SalesOutboundList.vue'),
        },
        {
          path: 'order/sales-outbound/detail/:id',
          name: 'SalesOutboundDetail',
          component: () => import('@/views/order/SalesOutboundDetail.vue'),
        },
        {
          path: 'order/product/list',
          name: 'ProductOrderList',
          component: () => import('@/views/order/ProductOrderList.vue'),
        },
        {
          path: 'order/product/add',
          name: 'ProductOrderAdd',
          component: () => import('@/views/order/ProductOrderForm.vue'),
        },
        {
          path: 'order/product/detail/:id',
          name: 'ProductOrderDetail',
          component: () => import('@/views/order/ProductOrderDetail.vue'),
        },
        {
          path: 'order/product/edit/:id',
          name: 'ProductOrderEdit',
          component: () => import('@/views/order/ProductOrderForm.vue'),
        },
        {
          path: 'user/level',
          name: 'UserLevel',
          component: () => import('@/views/user/UserLevel.vue'),
        },
        {
          path: 'user/category',
          name: 'UserCategory',
          component: () => import('@/views/user/UserCategory.vue'),
        },
        {
          path: 'user/industry',
          name: 'UserIndustry',
          component: () => import('@/views/user/UserIndustry.vue'),
        },
        {
          path: 'user/tag',
          name: 'UserTag',
          component: () => import('@/views/user/UserTag.vue'),
        },
        {
          path: 'user/property',
          name: 'UserProperty',
          component: () => import('@/views/user/UserProperty.vue'),
        },
        {
          path: 'user/source-channel',
          name: 'UserSourceChannel',
          component: () => import('@/views/user/UserSourceChannel.vue'),
        },
        {
          path: 'channel/level',
          name: 'DealerLevel',
          component: () => import('@/views/channel/DealerLevel.vue'),
        },
        {
          path: 'channel/benefit',
          name: 'DealerBenefit',
          component: () => import('@/views/channel/DealerBenefit.vue'),
        },
        {
          path: 'channel/category',
          name: 'DealerCategory',
          component: () => import('@/views/channel/DealerCategory.vue'),
        },
        {
          path: 'channel/source-channel',
          name: 'DealerSourceChannel',
          component: () => import('@/views/channel/DealerSourceChannel.vue'),
        },
        {
          path: 'channel/industry',
          name: 'DealerIndustry',
          component: () => import('@/views/channel/DealerIndustry.vue'),
        },
        {
          path: 'channel/property',
          name: 'DealerProperty',
          component: () => import('@/views/channel/DealerProperty.vue'),
        },
        {
          path: 'channel/list',
          name: 'DealerList',
          component: () => import('@/views/channel/DealerList.vue'),
        },
        {
          path: 'channel/create',
          name: 'DealerCreate',
          component: () => import('@/views/channel/DealerForm.vue'),
        },
        {
          path: 'channel/edit/:id',
          name: 'DealerEdit',
          component: () => import('@/views/channel/DealerForm.vue'),
        },
        {
          path: 'channel/detail/:id',
          name: 'DealerDetail',
          component: () => import('@/views/channel/DealerDetail.vue'),
        },
        {
          path: 'channel/contract',
          name: 'DealerContractList',
          component: () => import('@/views/channel/DealerContractList.vue'),
        },
        {
          path: 'channel/contract/detail/:id',
          name: 'DealerContractDetail',
          component: () => import('@/views/channel/DealerContractDetail.vue'),
        },
        {
          path: 'channel/termination',
          name: 'DealerTerminationList',
          component: () => import('@/views/channel/DealerTerminationList.vue'),
        },
        {
          path: 'report/lead',
          name: 'LeadReportList',
          component: () => import('@/views/report/LeadReportList.vue'),
        },
        {
          path: 'report/lease-business-analysis',
          name: 'LeaseBusinessAnalysis',
          component: () => import('@/views/report/LeaseBusinessAnalysis.vue'),
        },
        {
          path: 'report/dealer-performance',
          name: 'DealerPerformanceList',
          component: () => import('@/views/report/DealerPerformanceList.vue'),
        },
        {
          path: 'report/fund-income-expenditure',
          name: 'FundIncomeExpenditure',
          component: () => import('@/views/report/FundIncomeExpenditure.vue'),
        },
        {
          path: 'report/sales-performance',
          name: 'SalesPerformanceList',
          component: () => import('@/views/report/SalesPerformanceList.vue'),
        },
        ...reportBizV2Routes,
        {
          path: 'report/operations-analysis',
          name: 'OperationsAnalysis',
          component: () => import('@/views/report/OperationsAnalysis.vue'),
        },
        {
          path: 'system/department',
          name: 'DepartmentManage',
          component: () => import('@/views/system/DepartmentManage.vue'),
        },
        {
          path: 'system/role',
          name: 'RoleManage',
          component: () => import('@/views/system/RoleManage.vue'),
        },
        {
          path: 'system/account',
          name: 'AccountManage',
          component: () => import('@/views/system/AccountManage.vue'),
        },
        {
          path: 'system/role/:id/permission',
          name: 'RolePermissionAssign',
          component: () => import('@/views/system/RolePermissionAssign.vue'),
        },
        {
          path: 'system/employee',
          redirect: 'system/department',
        },
        {
          path: 'system/employee/target',
          name: 'EmployeeTargetManage',
          component: () => import('@/views/system/EmployeeTargetManage.vue'),
        },
        {
          path: 'system/target-settings',
          name: 'TargetSettings',
          component: () => import('@/views/system/TargetSettings.vue'),
        },
        {
          path: 'system/message-list',
          name: 'MessageList',
          component: () => import('@/views/message/MessageList.vue'),
        },
        {
          path: 'system/message-config',
          name: 'MessageConfig',
          component: () => import('@/views/message/MessageConfig.vue'),
        },
        {
          path: 'system/other-settings',
          name: 'OtherSettings',
          component: () => import('@/views/system/OtherSettings.vue'),
        },
        {
          path: 'system/area',
          name: 'AreaManage',
          component: () => import('@/views/system/AreaManage.vue'),
        },
        {
          path: 'system/large-area',
          name: 'LargeAreaManage',
          component: () => import('@/views/system/LargeAreaManage.vue'),
        },
        {
          path: 'system/login-log',
          name: 'LoginLogManage',
          component: () => import('@/views/system/LoginLogManage.vue'),
        },
        {
          path: 'system/operation-log',
          name: 'OperationLogManage',
          component: () => import('@/views/system/OperationLogManage.vue'),
        },
        {
          path: 'finance/fund-account',
          name: 'FundAccountList',
          component: () => import('@/views/finance/FundAccountList.vue'),
        },
        {
          path: 'finance/dealer-recharge-application',
          name: 'DealerRechargeApplicationList',
          component: () => import('@/views/finance/DealerRechargeApplicationList.vue'),
        },
        {
          path: 'finance/pre-deduction',
          name: 'PreDeductionList',
          component: () => import('@/views/finance/PreDeductionList.vue'),
        },
        {
          path: 'finance/withdrawal-application',
          name: 'WithdrawalApplicationList',
          component: () => import('@/views/finance/WithdrawalApplicationList.vue'),
        },
        {
          path: 'finance/fund-flow',
          name: 'FundFlowList',
          component: () => import('@/views/finance/FundFlowList.vue'),
        },
        {
          path: 'finance/invoice',
          name: 'InvoiceList',
          component: () => import('@/views/finance/InvoiceList.vue'),
        },
        {
          path: 'finance/monthly-statement',
          name: 'MonthlyStatementList',
          component: () => import('@/views/finance/MonthlyStatementList.vue'),
        },
        {
          path: 'workorder/manage',
          name: 'WorkorderManage',
          component: () => import('@/views/workorder/WorkorderManage.vue'),
        },
        {
          path: 'workorder/service',
          name: 'ServiceWorkorder',
          component: () => import('@/views/workorder/ServiceWorkorder.vue'),
        },
        {
          path: 'workorder/service/detail/:id',
          name: 'ServiceWorkorderDetail',
          component: () => import('@/views/workorder/ServiceWorkorderDetail.vue'),
        },
        {
          path: 'workorder/service/create',
          name: 'ServiceWorkorderCreate',
          component: () => import('@/views/workorder/ServiceWorkorderForm.vue'),
        },
        {
          path: 'workorder/service/edit/:id',
          name: 'ServiceWorkorderEdit',
          component: () => import('@/views/workorder/ServiceWorkorderForm.vue'),
        },
        {
          path: 'workorder/settlement',
          name: 'ServiceSettlement',
          component: () => import('@/views/workorder/ServiceSettlement.vue'),
        },
        {
          path: 'workorder/engineer',
          name: 'EngineerManage',
          component: () => import('@/views/workorder/EngineerManage.vue'),
        },
        {
          path: 'workorder/engineer-level',
          name: 'EngineerLevel',
          component: () => import('@/views/workorder/engineerlevel/EngineerLevelList.vue'),
        },
        {
          path: 'workorder/dispatcher',
          name: 'DispatcherManage',
          component: () => import('@/views/workorder/DispatcherManage.vue'),
        },
        {
          path: 'workorder/subject',
          name: 'ServiceSubject',
          component: () => import('@/views/workorder/subject/SubjectList.vue'),
        },
        {
          path: 'workorder/tag',
          name: 'WorkorderTag',
          component: () => import('@/views/workorder/WorkorderTag.vue'),
        },
        {
          path: 'workorder/complaint-category',
          name: 'ComplaintCategory',
          component: () => import('@/views/workorder/ComplaintCategory.vue'),
        },
        {
          path: 'workorder/aftersale-category',
          name: 'AfterSaleCategory',
          component: () => import('@/views/workorder/AfterSaleCategory.vue'),
        },
        {
          path: 'workorder/aftersales-manage',
          name: 'AfterSalesManage',
          component: () => import('@/views/workorder/AfterSalesManage.vue'),
        },
        {
          path: 'workorder/complaint-manage',
          name: 'ComplaintManage',
          component: () => import('@/views/workorder/ComplaintManage.vue'),
        },
        {
          path: 'iot/contract',
          name: 'IotContractManage',
          component: () => import('@/views/iot/IotContractManage.vue'),
        },
        {
          path: 'iot/contract/create',
          name: 'IotContractCreate',
          component: () => import('@/views/iot/IotContractCreate.vue'),
        },
        {
          path: 'iot/contract/detail/:id',
          name: 'IotContractDetail',
          component: () => import('@/views/iot/IotContractDetail.vue'),
        },
        {
          path: 'iot/contract/remove-device/:id',
          name: 'IotContractRemoveDevice',
          component: () => import('@/views/iot/IotContractRemoveDevice.vue'),
        },
        {
          path: 'iot/termination-reason',
          name: 'IotTerminationReasonManage',
          component: () => import('@/views/iot/IotTerminationReasonManage.vue'),
        },
        {
          path: 'iot/traffic-pool',
          name: 'TrafficPoolManage',
          component: () => import('@/views/iot/TrafficPoolManage.vue'),
        },
        {
          path: 'iot/traffic-recharge',
          name: 'TrafficRecharge',
          component: () => import('@/views/iot/TrafficRecharge.vue'),
        },
        {
          path: 'iot/device-install',
          name: 'DeviceInstall',
          component: () => import('@/views/iot/DeviceInstall.vue'),
        },
        {
          path: 'iot/device',
          name: 'DeviceManage',
          component: () => import('@/views/iot/DeviceManage.vue'),
        },
        {
          path: 'iot/device/detail/:id',
          name: 'DeviceDetail',
          component: () => import('@/views/iot/DeviceDetail.vue'),
        },
        {
          path: 'iot/protocol',
          name: 'ProtocolManage',
          component: () => import('@/views/iot/ProtocolManage.vue'),
        },
        {
          path: 'iot/instruction-set',
          name: 'InstructionSetManage',
          component: () => import('@/views/iot/InstructionSetManage.vue'),
        },
        {
          path: 'iot/log',
          name: 'IotLogManage',
          component: () => import('@/views/iot/IotLogManage.vue'),
        },
      ],
    },
    {
      path: '/screen/realtime',
      name: 'ScreenRealtime',
      component: () => import('@/views/screen/realtime.vue'),
      meta: { title: '实时大屏' },
    },
    {
      path: '/screen/demo',
      name: 'ScreenDemo',
      component: () => import('@/views/screen/index.vue'),
      meta: { title: '演示大屏' },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: to => (to.path.startsWith('/main') ? '/main/dashboard' : '/login'),
    },
  ],
})

patchRouterPromiseMethod(router, 'push')
patchRouterPromiseMethod(router, 'replace')

/** 免权限白名单路由（无需权限检查） */
const PUBLIC_PATHS = ['/login', '/', '/main/dashboard']

router.beforeEach((to, from, next) => {
  // 白名单路由直接放行
  if (PUBLIC_PATHS.includes(to.path)) return next()
  // 后台与大屏路由需要权限检查，其余路由不检查
  if (!to.path.startsWith('/main') && !to.path.startsWith('/screen')) return next()
  // 权限检查
  if (!canAccessRoute(to.path)) {
    const token = localStorage.getItem('token')
    if (token) {
      ElMessage.warning('您没有访问该页面的权限，已跳转到首页')
      next('/main/dashboard')
    } else {
      next('/login')
    }
    return
  }
  next()
})

router.afterEach(to => {
  syncDocumentTitle(to)
})

export default router
